import type { BaseType } from 'antd/lib/typography/Base';
import { useFormikContext } from 'formik';
import { type JSX, useCallback, useMemo } from 'react';

import './SpecificationDimensionsSimpleField.scss';

import { SelectField } from 'src/components/base/fields/SelectField';
import type { MeasureUnitDimensionsValue } from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsField.types';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import { ValidationError } from 'src/components/base/fields/common/ValidationError/ValidationError';
import { cNs } from 'src/helpers';
import type { ISelectOption } from 'src/types';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  type SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function OptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

type SpecificationDimensionsSimpleFieldProps = {
  commonFieldProps: {
    errorType: BaseType;
    name: string;
    label?: string;
    description: string | null;
    disabled?: boolean;
  };

  is3d: boolean;

  allowedMeasureUnits: SpecificationMeasureUnit[];

  containerClassName?: string;
  className?: string;
};

export function SpecificationDimensionsSimpleField({
  commonFieldProps,
  is3d,
  allowedMeasureUnits,
  containerClassName,
  className,
}: Readonly<SpecificationDimensionsSimpleFieldProps>): JSX.Element {
  const { values, setValues } = useFormikContext<Record<string, MeasureUnitDimensionsValue>>();

  const measureUnitsOptions: ISelectOption[] = useMemo(
    () =>
      allowedMeasureUnits.map((measureUnit) => ({
        label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[measureUnit],
        value: measureUnit,
      })),
    [allowedMeasureUnits]
  );

  const handleMeasureUnitChange = useCallback(
    (newMeasureUnit: SpecificationMeasureUnit) => {
      const currentValue = values[commonFieldProps.name];

      if (currentValue == null) {
        return;
      }

      void setValues((prev) => ({
        ...prev,

        [commonFieldProps.name]: {
          measureUnit: newMeasureUnit,

          length: null as unknown as number,
          width: null as unknown as number,

          height: currentValue.height != null ? (null as unknown as number) : undefined,
        },
      }));
    },
    [values, commonFieldProps.name, setValues]
  );

  return (
    <div className={cNs('specification-dimensions-simple-field__container', containerClassName)}>
      <div className="specification-dimensions-simple-field__label">{commonFieldProps.label}</div>
      {commonFieldProps.description && (
        <div className="specification-dimensions-simple-field__description">{commonFieldProps.description}</div>
      )}
      <div className={cNs('flex row nowrap gap-small', className)}>
        <TextInputField
          name={`${commonFieldProps.name}.length`}
          type="number"
          placeholder="Length"
          isShowErrorMessage={false}
          disabled={commonFieldProps.disabled}
        />
        <TextInputField
          name={`${commonFieldProps.name}.width`}
          type="number"
          placeholder="Width"
          isShowErrorMessage={false}
          disabled={commonFieldProps.disabled}
        />
        {is3d && (
          <TextInputField
            name={`${commonFieldProps.name}.height`}
            type="number"
            placeholder="Height"
            isShowErrorMessage={false}
            disabled={commonFieldProps.disabled}
          />
        )}
        <SelectField
          style={{ minWidth: 65 }}
          name={`${commonFieldProps.name}.measureUnit`}
          options={measureUnitsOptions}
          onChange={handleMeasureUnitChange}
          disabled={commonFieldProps.disabled}
          popupMatchSelectWidth={false}
          optionRender={({ value }) => <OptionRender value={value} />}
        />
      </div>
      <div className="relative">
        <ValidationError {...commonFieldProps} />
      </div>
    </div>
  );
}
